import React from "react";
import { Link } from "react-router-dom";
import { useReveal } from "./useReveal";
import "./VisionPage.css";

/**
 * THE VISION — what Everything Eco will become.
 * Everything here is deliberately future-framed: dreams labeled as dreams,
 * plans labeled as plans. Phase 1 is the only "now" — and it's real.
 */

const PHASES = [
  {
    n: "01", when: "NOW", now: true, title: "One founder. One neighborhood.",
    items: ["Free pickups — e-waste, clothing, furniture", "The website, the game, the characters", "Building trust one door at a time", "Everything logged, everything honest"],
  },
  {
    n: "02", when: "NEXT", now: false, title: "The operation grows.",
    items: ["Trucks on the road & first employees", "A warehouse for sorting & refurbishing", "Daily pickups across the boroughs", "The Everything Eco app"],
  },
  {
    n: "03", when: "THEN", now: false, title: "More than pickups.",
    items: ["Community centers & technology labs", "Youth programs & education", "Food assistance & neighborhood support", "Refurbished tech for families who need it"],
  },
  {
    n: "04", when: "BIGGER", now: false, title: "Partnered with the city.",
    items: ["Schools, hospitals & NYCHA", "Business & government partnerships", "Workforce development at scale", "A model other cities can copy"],
  },
  {
    n: "05", when: "THE DREAM", now: false, title: "Every city. Every neighborhood.",
    items: ["National expansion", "Because every city has neighborhoods full of value", "— and people willing to recognize it"],
  },
];

const PROGRAMS = [
  "Basketball", "Chess club", "Coding & AI", "CompTIA / IT certs", "Resume workshops",
  "Interview practice", "HVAC & trades", "Financial literacy", "Credit & taxes",
  "Entrepreneurship", "English / Español", "Senior assistance", "Food drives",
  "Community fridges", "Back-to-school drives", "Holiday events", "Community gardens", "Scholarships",
];

const FUTURE_SERVICES = [
  "Composting", "Food rescue", "Bike recycling", "Battery drop-offs", "Appliance recycling",
  "Solar panel recycling", "Furniture restoration", "Repair services", "Community tool library",
  "Toy donations", "School supply drives", "Disaster relief collection", "Construction material recycling",
];

const APP_FEATURES = [
  ["📦", "Book pickups in two taps"],
  ["💎", "EcoPoints & rewards"],
  ["📊", "Your personal impact tracker"],
  ["🧾", "Donation history & tax receipts"],
  ["📅", "Community events & volunteering"],
  ["🤖", "Earth Jr. AI assistant"],
];

const VisionPage: React.FC = () => {
  useReveal();
  return (
    <div className="vp-page">
      {/* ── Hero ── */}
      <section className="vp-hero">
        <div className="vp-orb a" /><div className="vp-orb b" />
        <div className="ee-container vp-hero-inner">
          <div className="vp-kicker">🔭 THE VISION</div>
          <h1>
            Recycling is chapter one.<br />
            <span className="vp-grad">Here's the whole book.</span>
          </h1>
          <p className="vp-sub">
            Everything Eco starts with a pickup truck and free pickups. It doesn't end there.
            This page is our long-term vision — labeled honestly as exactly that. We'd rather
            show you the dream and grow into it than pretend we're already there.
          </p>
        </div>
      </section>

      {/* ── Phases ── */}
      <section className="ee-section vp-phases">
        <div className="ee-container">
          <div className="ee-section-label" style={{ textAlign: "center" }}>The road ahead</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>Five phases. We're in Phase 1.</h2>
          <div className="vp-phase-list">
            {PHASES.map((p) => (
              <div key={p.n} className={`vp-phase${p.now ? " now" : ""}`}>
                <div className="vp-phase-side">
                  <span className="vp-phase-n">{p.n}</span>
                  <span className={`vp-phase-when${p.now ? " live" : ""}`}>{p.now ? "● YOU ARE HERE" : p.when}</span>
                </div>
                <div className="vp-phase-body">
                  <h3>{p.title}</h3>
                  <ul>
                    {p.items.map((it) => <li key={it}>{it}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Two engines ── */}
      <section className="ee-section vp-engines">
        <div className="ee-container">
          <div className="ee-section-label" style={{ textAlign: "center" }}>How we plan to build it</div>
          <h2 className="ee-section-title" style={{ textAlign: "center" }}>Two engines, one mission</h2>
          <p className="vp-engines-sub">
            As Everything Eco grows, we plan to run it as two connected organizations —
            a business that sustains itself, and a nonprofit that gives back.
          </p>
          <div className="vp-engine-grid">
            <div className="vp-engine biz">
              <div className="vp-engine-tag">THE BUSINESS · LLC</div>
              <h3>Everything Eco</h3>
              <p>
                The engine that keeps the lights on: pickups, business services, secure data
                destruction, refurbishing. It creates local jobs, pays fair wages, and funds
                the growth — so the mission never depends on charity alone.
              </p>
            </div>
            <div className="vp-engine np">
              <div className="vp-engine-tag">THE NONPROFIT · PLANNED</div>
              <h3>Everything Eco Foundation</h3>
              <p>
                The engine that gives back: community cleanups, youth programs, environmental
                education, technology donations, workforce training, scholarships. Built so the
                neighborhood that raised us rises with us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Programs ── */}
      <section className="ee-section vp-programs">
        <div className="ee-container" style={{ textAlign: "center" }}>
          <div className="ee-section-label" style={{ textAlign: "center" }}>Dreams, on the record</div>
          <h2 className="ee-section-title">The programs we intend to run</h2>
          <p className="vp-programs-sub">
            Every one of these has come up in the founder's notebooks. We're writing them here
            so you can hold us to it.
          </p>
          <div className="vp-chip-cloud">
            {PROGRAMS.map((p) => <span key={p} className="vp-chip">{p}</span>)}
          </div>

          <h2 className="ee-section-title" style={{ marginTop: 64 }}>…and the services we'll add</h2>
          <p className="vp-programs-sub">
            Today it's e-waste, clothing, furniture, and cleanouts. As we grow, so does what we can
            take off your hands — and give a second life.
          </p>
          <div className="vp-chip-cloud">
            {FUTURE_SERVICES.map((s) => <span key={s} className="vp-chip alt">{s}</span>)}
          </div>
        </div>
      </section>

      {/* ── App preview ── */}
      <section className="ee-section vp-app">
        <div className="ee-container vp-app-grid">
          <div className="vp-app-copy">
            <div className="ee-section-label">On the roadmap</div>
            <h2 className="ee-section-title">The Everything Eco app</h2>
            <p>
              Phase 2 brings Everything Eco to your pocket. One app for booking, rewards,
              impact, events — with Earth Jr. as your guide.
            </p>
            <ul className="vp-app-list">
              {APP_FEATURES.map(([ico, label]) => (
                <li key={label}><span>{ico}</span>{label}</li>
              ))}
            </ul>
            <Link to="/#newsletter" className="ee-btn ee-btn-green">🔔 Tell me when it launches</Link>
          </div>
          <div className="vp-phone">
            <div className="vp-iphone">
              <span className="vp-iphone-btn mute" aria-hidden="true" />
              <span className="vp-iphone-btn vol-up" aria-hidden="true" />
              <span className="vp-iphone-btn vol-dn" aria-hidden="true" />
              <span className="vp-iphone-btn power" aria-hidden="true" />
              <div className="vp-iphone-screen">
                <div className="vp-iphone-island" aria-hidden="true" />
                <div className="vp-iphone-status" aria-hidden="true">
                  <span className="vp-time">9:41</span>
                  <span className="vp-signal">
                    <i className="b1" /><i className="b2" /><i className="b3" /><i className="b4" />
                    <em className="vp-battery"><em className="vp-battery-fill" /></em>
                  </span>
                </div>
                <div className="vp-iphone-content">
                  <img src={`${process.env.PUBLIC_URL}/earthjr-wave.png`} alt="Earth Jr. waving inside the future app" />
                  <strong>Everything Eco</strong>
                  <span>Coming to your pocket</span>
                  <div className="vp-phone-btn">📦 Book a pickup</div>
                  <div className="vp-phone-btn alt">💎 My EcoPoints</div>
                </div>
                <div className="vp-iphone-home" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Legacy ── */}
      <section className="ee-section vp-legacy">
        <div className="ee-container vp-legacy-inner">
          <div className="vp-legacy-kicker">WHY IT MATTERS</div>
          <p className="vp-legacy-quote">
            This was never about building the biggest company. It's about proving that someone
            from <em>Williamsburg</em> can build something that helps thousands of people —
            and opens doors for the next generation to walk through.
          </p>
          <p className="vp-legacy-sign">— The promise behind Everything Eco</p>
          <div className="vp-legacy-btns">
            <Link to="/pickup" className="ee-btn ee-btn-green">📦 Start with one pickup</Link>
            <Link to="/mission" className="ee-btn vp-btn-ghost">💚 Read the mission</Link>
            <Link to="/contact" className="ee-btn vp-btn-ghost">🤝 Build with us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisionPage;
